import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useGetMotoboysList } from './api/get-motoboys';
import { Filter } from './components/filter';
import { List } from './components/list';
import type {
  MotoboysListParams,
  MotoboysQueryString,
  TypeMotoboysListForm,
} from './types';
import { GET_MOTOBOYS_LIST } from './constants';
import {
  queryStringify,
  queryStringToObject,
} from '@/_config/lib/query-string';
import { useAppSelector } from '@/_store/hooks';

function MotoboysList() {
  const queryClient = useQueryClient();
  const queryString: MotoboysQueryString = queryStringToObject(location.search);
  const [, setSearchParams] = useSearchParams();

  const filteredCityId = useAppSelector(
    state => state.filters.cityIdOnDeliverymanList,
  );
  const filteredDeliveryType = useAppSelector(
    state => state.filters.deliveryTypeOnDeliverymanList,
  );

  useEffect(() => {
    if (filteredCityId) {
      setSearchParams(prev => {
        prev.set('cityId', filteredCityId);
        return prev;
      });
    }

    if (filteredDeliveryType) {
      setSearchParams(prev => {
        prev.set('deliveryType', filteredDeliveryType);
        return prev;
      });
    }
  }, [filteredCityId, filteredDeliveryType]);

  const [params, setParams] = useState<MotoboysListParams>({
    cpf: queryString.cpf || '',
    cellphone: queryString.cellphone || '',
    status: queryString.status || undefined,
    deliverymanId: queryString.deliverymanId || '',
    cityId: filteredCityId || queryString.cityId || '',
    name: queryString.name || '',
    endDate: queryString.endDate ? new Date(queryString.endDate) : undefined,
    startDate: queryString.startDate
      ? new Date(queryString.startDate)
      : undefined,
    deliveryType: filteredDeliveryType || queryString.deliveryType || '',
  });

  const [page, setPage] = useState(Number(queryString.page) || 1);

  const { data, isLoading, isFetching } = useGetMotoboysList(
    {
      ...params,
      page,
    },
    {
      staleTime: Infinity,
    },
  );

  function onRequest(data: TypeMotoboysListForm) {
    const {
      name,
      cpf,
      endDate,
      startDate,
      status,
      cityId,
      cellphone,
      deliverymanId,
      deliveryType,
    } = data;

    const queries = {
      name,
      cpf,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      cityId,
      deliverymanId,
      status,
      cellphone,
      deliveryType,
    };

    queryClient.invalidateQueries([GET_MOTOBOYS_LIST, queries]);

    setParams(queries as MotoboysListParams);

    const stringified = queryStringify(queries);

    setSearchParams(stringified);

    setPage(1);
  }

  return (
    <div>
      <Filter {...{ params, onRequest }} />

      <List
        page={page}
        setPage={newPage => {
          setPage(newPage);
          const stringified = queryStringify({ ...params, page: newPage });
          setSearchParams(stringified);
        }}
        isLoading={isLoading || isFetching}
        motoboys={data}
      />
    </div>
  );
}

export { MotoboysList };
